import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, X, Video as VideoIcon, Upload, Loader2, CheckCircle, Wand2, MessageSquare, Send, FileText, ArrowRight, ArrowLeft, Circle, UserPlus } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { RecordingModal } from '@/components/recording/RecordingModal';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';
import { ToastAction } from '@/components/ui/toast';
import {
  getProductFileUploadUrl, requestMuxIngest, conciergeOutline, conciergeChat, conciergeLesson, conciergeScript, conciergeCurriculumChat,
  type CourseModule, type CourseLesson, type ConciergeOutline, type ConciergeChatMessage, type ConciergeCurriculum,
} from '@/services/creatorApi';
import { createInterview } from '@/services/interviewApi';
import { useToast } from '@/hooks/use-toast';

interface Props {
  open: boolean;
  onClose: () => void;
  courseTitle: string;
  courseDescription: string;
  modules: CourseModule[];
  applyOutline: (o: ConciergeOutline) => void;
  updateLesson: (moduleId: string, lessonId: string, patch: Partial<CourseLesson>) => void;
  /** The creator's typed idea from the AI chooser — seeds the intake so "Set it up" carries their context. */
  initialIdea?: string;
}

type Phase = 'idea' | 'outline' | 'chat' | 'lesson' | 'done';

// A course-chat message; assistant turns may carry a proposed full-curriculum
// outline the creator can apply.
type CourseChatMsg = ConciergeChatMessage & { outline?: ConciergeOutline | null };

const primaryBtn: React.CSSProperties = { padding: '13px 24px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 50 };
const ghostBtn: React.CSSProperties = { padding: '12px 20px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 46 };

// Full-screen guided course builder. Takes over the screen and walks the creator
// through their course one lesson at a time — draft the outline, then for each
// lesson: talk it through (optional), record (with a teleprompter script),
// polish, and auto-advance. Non-pushy (feedback_concierge_non_pushy): every step
// is skippable and there's always a "save & exit" out.
export default function CourseConciergePanel({ open, onClose, courseTitle, courseDescription, modules, applyOutline, updateLesson, initialIdea }: Props) {
  const { toast } = useToast();
  const [phase, setPhase] = useState<Phase>('idea');
  const [cursor, setCursor] = useState(0);
  const [idea, setIdea] = useState('');
  // Light intake so the outline is scaled to the creator, not generated whole
  // from one sentence. All optional (feedback_concierge_non_pushy).
  const [audience, setAudience] = useState('');
  const [outcomes, setOutcomes] = useState('');
  const [angle, setAngle] = useState('');
  const [depth, setDepth] = useState<'short' | 'standard' | 'full'>('standard');
  const [drafting, setDrafting] = useState(false);
  const [scripts, setScripts] = useState<Record<string, string>>({});

  const [recording, setRecording] = useState(false);
  const [uploadingLesson, setUploadingLesson] = useState(false);
  // Inline video edit drawer — one instance for the panel; the lesson being edited.
  const [editTarget, setEditTarget] = useState<{ moduleId: string; lessonId: string; s3Key: string; title?: string } | null>(null);

  // Coaching chat (scoped to the current lesson)
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<ConciergeChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const [busyAction, setBusyAction] = useState<'refine' | 'script' | null>(null);
  const [startingInterview, setStartingInterview] = useState(false);

  // Course-level curriculum chat — builds/revises the WHOLE curriculum through
  // conversation. Kept entirely separate from the per-lesson coaching chat above.
  const [courseChatMessages, setCourseChatMessages] = useState<CourseChatMsg[]>([]);
  const [courseChatInput, setCourseChatInput] = useState('');
  const [courseChatBusy, setCourseChatBusy] = useState(false);
  // Indices of chat messages whose proposed outline has been applied.
  const [appliedIdx, setAppliedIdx] = useState<Set<number>>(new Set());
  const courseChatInputRef = useRef<HTMLTextAreaElement>(null);

  // Flatten lessons for a linear walkthrough.
  const flat = modules.flatMap((m, mi) => m.lessons.map(l => ({ moduleId: m.id, moduleTitle: m.title, moduleIndex: mi, lesson: l })));
  const current = flat[cursor];

  // On open, jump to the right phase; reset chat when the lesson changes.
  useEffect(() => {
    if (open) {
      setPhase(modules.length ? 'outline' : 'idea');
      setCursor(0);
      // Seed the intake with what the creator typed in the AI chooser.
      if (initialIdea && !idea) setIdea(initialIdea);
    }
    /* eslint-disable-next-line */
  }, [open]);
  useEffect(() => { setChatOpen(false); setChatMessages([]); setChatInput(''); }, [cursor, phase]);

  if (!open) return null;

  const recordedCount = flat.filter(f => !!f.lesson.s3Key).length;
  const convoNote = () => chatMessages.map(m => `${m.role === 'user' ? 'Me' : 'Coach'}: ${m.content}`).join('\n').slice(0, 1500);
  // Lessons that already have footage — used to badge proposed-outline lessons
  // that map back to a recorded lesson, and (server-side) to protect them.
  const recordedLessonIds = new Set(flat.filter(f => !!f.lesson.s3Key).map(f => f.lesson.id));
  // Only the most recent proposed outline is applicable; earlier ones are stale.
  let latestOutlineIdx = -1;
  courseChatMessages.forEach((m, i) => { if (m.outline && m.outline.modules?.length) latestOutlineIdx = i; });

  const handleDraft = async () => {
    if (!idea.trim() || drafting) return;
    setDrafting(true);
    const o = await conciergeOutline(idea.trim(), { audience: audience.trim(), outcomes: outcomes.trim(), angle: angle.trim(), depth });
    setDrafting(false);
    if (o.modules && o.modules.length) { applyOutline(o); setPhase('outline'); }
    else toast({ title: "Couldn't draft that", description: 'Try rephrasing, or build it yourself.', variant: 'destructive' });
  };

  const advance = () => {
    if (cursor < flat.length - 1) setCursor(cursor + 1);
    else setPhase('done');
  };
  const back = () => {
    if (cursor > 0) setCursor(cursor - 1);
    else setPhase('outline');
  };

  // --- coaching ---
  const openChat = () => {
    setChatOpen(true);
    setChatMessages([{ role: 'assistant', content: `Happy to help you shape “${current?.lesson.title || 'this lesson'}.” What do you want it to get across — or if you're ready, just record.` }]);
  };
  const sendChat = async () => {
    if (!chatInput.trim() || chatBusy || !current) return;
    const next = [...chatMessages, { role: 'user' as const, content: chatInput.trim() }];
    setChatMessages(next); setChatInput(''); setChatBusy(true);
    const { reply } = await conciergeChat(next, { courseTitle, moduleTitle: current.moduleTitle, lessonTitle: current.lesson.title, lessonDescription: current.lesson.description });
    setChatBusy(false);
    if (reply) setChatMessages([...next, { role: 'assistant', content: reply }]);
  };
  const refineLesson = async () => {
    if (!current) return; setBusyAction('refine');
    const r = await conciergeLesson({ courseTitle, moduleTitle: current.moduleTitle, lessonTitle: current.lesson.title, note: convoNote() });
    setBusyAction(null);
    if (r.title || r.description) { updateLesson(current.moduleId, current.lesson.id, { title: r.title || current.lesson.title, description: r.description || current.lesson.description }); toast({ title: 'Lesson refined' }); }
  };
  const generateScript = async () => {
    if (!current) return; setBusyAction('script');
    const { script } = await conciergeScript({ courseTitle, moduleTitle: current.moduleTitle, lessonTitle: current.lesson.title, lessonDescription: current.lesson.description, note: convoNote() });
    setBusyAction(null);
    if (script) { setScripts(s => ({ ...s, [current.lesson.id]: script })); toast({ title: 'Script ready', description: "It'll be in your teleprompter when you record." }); }
  };

  // --- whole-curriculum chat ---
  // Snapshot the live curriculum for the model — ids let it reuse structure,
  // hasVideo tells it which lessons already have footage (never drop those).
  const buildCurriculum = (): ConciergeCurriculum => ({
    title: courseTitle,
    description: courseDescription,
    modules: modules.map(m => ({
      id: m.id,
      title: m.title,
      lessons: m.lessons.map(l => ({ id: l.id, title: l.title, description: l.description || '', hasVideo: !!l.s3Key })),
    })),
  });
  const enterCourseChat = () => {
    if (!courseChatMessages.length) {
      const seed = modules.length
        ? `You've got ${modules.length} module${modules.length !== 1 ? 's' : ''} so far — tell me where you want to take it. I can extend it, reorganize it, or tighten it up.`
        : `Let's shape your course together. Tell me what you want to teach — or ask me to draft, extend, or reorganize the curriculum and I'll lay it out for you.`;
      setCourseChatMessages([{ role: 'assistant', content: seed }]);
    }
    setPhase('chat');
  };
  const sendCourseChat = async () => {
    if (!courseChatInput.trim() || courseChatBusy) return;
    const next: CourseChatMsg[] = [...courseChatMessages, { role: 'user', content: courseChatInput.trim() }];
    setCourseChatMessages(next); setCourseChatInput(''); setCourseChatBusy(true);
    const { reply, outline } = await conciergeCurriculumChat(next.map(m => ({ role: m.role, content: m.content })), buildCurriculum());
    setCourseChatBusy(false);
    const proposed = outline && outline.modules && outline.modules.length ? outline : null;
    if (reply || proposed) {
      setCourseChatMessages([...next, { role: 'assistant', content: reply || "Here's a revised curriculum — take a look.", outline: proposed }]);
    }
  };
  const applyCourseOutline = (idx: number, outline: ConciergeOutline) => {
    applyOutline(outline);
    setAppliedIdx(prev => new Set(prev).add(idx));
    toast({ title: 'Curriculum updated' });
  };

  // --- recording / polish / upload ---
  const uploadLessonFile = async (file: File): Promise<Partial<CourseLesson>> => {
    const { uploadUrl, key, fileType } = await getProductFileUploadUrl(file.name, file.type);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener('load', () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed'))));
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('PUT', uploadUrl); xhr.setRequestHeader('Content-Type', file.type); xhr.send(file);
    });
    let mux = null;
    if (fileType === 'video') mux = await requestMuxIngest({ s3Key: key, kind: 'paid' }).catch(() => null);
    return { s3Key: key, fileType: fileType as CourseLesson['fileType'], muxAssetId: mux?.muxAssetId, muxPlaybackId: mux?.muxPlaybackId || undefined, muxIngestStatus: mux?.muxIngestStatus };
  };
  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file || !current) return;
    setUploadingLesson(true);
    try { updateLesson(current.moduleId, current.lesson.id, await uploadLessonFile(file)); toast({ title: 'Added' }); advance(); }
    catch (err: any) { toast({ title: 'Upload failed', description: err.message, variant: 'destructive' }); }
    finally { setUploadingLesson(false); }
  };
  const bringOnGuest = async () => {
    if (!current || startingInterview) return;
    setStartingInterview(true);
    try {
      const { interview } = await createInterview({ title: current.lesson.title || courseTitle || 'Interview' });
      window.open(`/my-store/interviews/${interview._id}`, '_blank');
      toast({ title: 'Interview room ready', description: 'Opened in a new tab — invite your guest from there.' });
    } catch (err: any) {
      toast({ title: "Couldn't start", description: err.message, variant: 'destructive' });
    } finally { setStartingInterview(false); }
  };
  // Record → save directly (no forced editor). Editing is one optional click
  // away via the toast action or the lesson's "Edit video" action.
  const handleRecorded = async (file: File) => {
    setRecording(false);
    const c = current;
    if (!c) return;
    setUploadingLesson(true);
    try {
      const video = await uploadLessonFile(file);
      updateLesson(c.moduleId, c.lesson.id, video);
      if (video.s3Key) {
        const target = { moduleId: c.moduleId, lessonId: c.lesson.id, s3Key: video.s3Key, title: c.lesson.title };
        toast({
          title: 'Recording saved',
          action: (
            <ToastAction altText="Edit video" onClick={() => setEditTarget(target)}>
              Edit video
            </ToastAction>
          ),
        });
      } else {
        toast({ title: 'Recording saved' });
      }
      advance();
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingLesson(false); }
  };

  const recorded = !!current?.lesson.s3Key;

  return (
    <div
      className="hj-event-paper flex flex-col"
      data-paper="beige"
      style={{
        background: '#fff',
        color: 'var(--hj-beige-ink)',
        border: '1px solid var(--hj-beige-line)',
        borderRadius: 'var(--hj-r-md)',
        overflow: 'hidden',
        // Remap the panel's dark-theme surfaces onto the beige paper.
        ['--hj-surface-1' as any]: '#FFFDF8',
        ['--hj-surface-2' as any]: 'var(--hj-beige)',
        ['--hj-hairline-soft' as any]: 'var(--hj-beige-line)',
      }}
    >
      {/* Top bar */}
      <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '12px 16px', borderBottom: '1px solid var(--hj-beige-line)' }}>
        <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-2)' }}>{/* Paper panel: brand yellow is invisible on white — use paper ink. */}
          <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink)' }} /> Build with me</div>
        {phase === 'lesson' && <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Lesson {cursor + 1} of {flat.length}</div>}
        <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-ink-3)', fontSize: 13 }}>Back to editor <X className="w-4 h-4" /></button>
      </div>
      {phase === 'lesson' && (
        <div style={{ height: 3, background: 'var(--hj-beige-line)', flexShrink: 0 }}>
          {/* Paper ink, not the yellow alias: a yellow fill on a beige track is
              barely distinguishable from the track. */}
          <div style={{ width: `${(cursor / flat.length) * 100}%`, height: '100%', background: 'var(--hj-beige-ink)', transition: 'width .3s ease' }} />
        </div>
      )}

      {/* Body */}
      <div className="overflow-y-auto flex flex-col items-center" style={{ padding: '28px 20px 36px', maxHeight: '72vh' }}>
        <div className="w-full" style={{ maxWidth: 660 }}>

          {phase === 'idea' && (
            <div className="text-center">
              <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(22px, 3vw, 30px)', color: 'var(--hj-ink)', letterSpacing: '-0.03em', fontWeight: 800, lineHeight: 1.05 }}>What do you want to teach?</h1>
              <p className="m-0" style={{ marginTop: 14, fontSize: 16, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>A sentence or two to start. Tell me a bit more and I'll shape the outline to fit — then we review it together before building.</p>
              <div data-paper="beige" style={{ marginTop: 24, borderRadius: 'var(--hj-r-md)', overflow: 'hidden', textAlign: 'left' }}>
                <Textarea value={idea} onChange={e => setIdea(e.target.value)} rows={3} placeholder="A self-tape masterclass for commercial actors — framing, reading the room, and slating." style={{ fontSize: 16 }} />
              </div>

              {/* Optional intake — scales the outline to the creator */}
              <div style={{ marginTop: 18, textAlign: 'left' }}>
                <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 10 }}>A few details (optional — skip any)</div>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink-2)', display: 'block', marginBottom: 5 }}>How big should it be?</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {([['short', 'A few lessons'], ['standard', 'Standard'], ['full', 'Full course']] as const).map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setDepth(k)} style={{ padding: '7px 14px', borderRadius: 999, fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', border: `1px solid ${depth === k ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`, background: depth === k ? 'var(--hj-blue)' : 'transparent', color: depth === k ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-2)' }}>{label}</button>
                      ))}
                    </div>
                  </div>
                  <div data-paper="beige" style={{ borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }}>
                    <Textarea value={audience} onChange={e => setAudience(e.target.value)} rows={1} placeholder="Who's it for? (e.g. new actors, working pros)" style={{ fontSize: 15 }} />
                  </div>
                  <div data-paper="beige" style={{ borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }}>
                    <Textarea value={outcomes} onChange={e => setOutcomes(e.target.value)} rows={2} placeholder="What should they walk away able to do? (2–3 outcomes)" style={{ fontSize: 15 }} />
                  </div>
                  <div data-paper="beige" style={{ borderRadius: 'var(--hj-r-md)', overflow: 'hidden' }}>
                    <Textarea value={angle} onChange={e => setAngle(e.target.value)} rows={1} placeholder="Your angle — what makes it yours? (optional)" style={{ fontSize: 15 }} />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center" style={{ marginTop: 24 }}>
                <button type="button" onClick={handleDraft} disabled={!idea.trim() || drafting} style={{ ...primaryBtn, opacity: (!idea.trim() || drafting) ? 0.5 : 1 }}>
                  {drafting ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting…</> : <><Wand2 className="w-4 h-4" /> Draft the outline</>}
                </button>
              </div>

              {/* Quieter alternative — talk the whole thing through instead of the form. */}
              <div className="flex items-center justify-center" style={{ marginTop: 16 }}>
                <button type="button" onClick={enterCourseChat} className="inline-flex items-center gap-1.5" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-ink)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }}>
                  <MessageSquare className="w-4 h-4" /> Rather talk it through? Chat with me instead <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {phase === 'outline' && (
            <div>
              <div className="mono-label text-center" style={{ color: 'var(--hj-ink-3)' }}>Your course</div>
              <h1 className="font-hj-display m-0 text-center" style={{ marginTop: 6, fontSize: 'clamp(20px, 2.6vw, 27px)', color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 800, lineHeight: 1.1 }}>{courseTitle.trim() || 'Your course outline'}</h1>
              <p className="m-0 text-center" style={{ marginTop: 12, fontSize: 15, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>Here's the plan. We'll go lesson by lesson from here — talk each one through and record it. Tweak anything later in the builder.</p>
              {/* Rework the whole curriculum through chat — reachable even with lessons already recorded. */}
              <div className="flex items-center justify-center" style={{ marginTop: 18 }}>
                <button type="button" onClick={enterCourseChat} style={primaryBtn}><MessageSquare className="w-4 h-4" /> Chat through the curriculum</button>
              </div>
              <div className="flex flex-col gap-4" style={{ marginTop: 28 }}>
                {modules.map((m, mi) => (
                  <div key={m.id} style={{ padding: 18, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
                    <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Module {mi + 1} · {m.title}</div>
                    <div className="flex flex-col gap-2" style={{ marginTop: 10 }}>
                      {m.lessons.map(l => (
                        <div key={l.id} className="flex items-center gap-2.5" style={{ fontSize: 14, color: 'var(--hj-ink)' }}>
                          {l.s3Key ? <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-green)' }} /> : <Circle className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-ink-4)' }} />}
                          {l.title}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-3" style={{ marginTop: 28 }}>
                <button type="button" onClick={() => { setCursor(0); setPhase('lesson'); }} disabled={!flat.length} style={{ ...primaryBtn, opacity: flat.length ? 1 : 0.5 }}>Start building <ArrowRight className="w-4 h-4" /></button>
                <button type="button" onClick={onClose} style={ghostBtn}>I'll take it from here</button>
              </div>
            </div>
          )}

          {phase === 'chat' && (
            <div>
              <div className="mono-label text-center" style={{ color: 'var(--hj-ink-3)' }}>Build with me</div>
              <h1 className="font-hj-display m-0 text-center" style={{ marginTop: 6, fontSize: 'clamp(20px, 2.6vw, 27px)', color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 800, lineHeight: 1.1 }}>Shape your curriculum</h1>
              <p className="m-0 text-center" style={{ marginTop: 12, fontSize: 15, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>Talk the whole course through — draft it, extend it, or reorganize it. I'll propose a layout you can apply when it's right. No rush, and you can build it by hand any time.</p>

              <div style={{ marginTop: 22, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                <div className="flex flex-col gap-2.5" style={{ padding: 16, maxHeight: 420, overflowY: 'auto' }}>
                  {courseChatMessages.map((m, i) => {
                    const hasOutline = !!(m.outline && m.outline.modules && m.outline.modules.length);
                    const isApplied = appliedIdx.has(i);
                    const isLatest = i === latestOutlineIdx;
                    const superseded = hasOutline && !isLatest && !isApplied;
                    return (
                      <React.Fragment key={i}>
                        <div style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: m.role === 'user' ? 'var(--hj-blue)' : 'var(--hj-surface-2)', color: m.role === 'user' ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)' }}>{m.content}</div>
                        {hasOutline && (
                          <div style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '94%', borderRadius: 12, background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden', opacity: superseded ? 0.5 : 1 }}>
                            <div style={{ padding: '13px 15px' }}>
                              <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', marginBottom: 12 }}>
                                <Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--hj-beige-ink)' }} /> Proposed curriculum
                              </div>
                              <div className="flex flex-col gap-3">
                                {m.outline!.modules.map((mod, mi) => (
                                  <div key={mi}>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{mi + 1}. {mod.title}</div>
                                    <div className="flex flex-col gap-1.5" style={{ marginTop: 6, paddingLeft: 14 }}>
                                      {mod.lessons.map((l, li) => (
                                        <div key={li} className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--hj-ink)' }}>
                                          <span>{l.title}</span>
                                          {l.id && recordedLessonIds.has(l.id) && (
                                            <span className="inline-flex items-center gap-1" style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--hj-green)', border: '1px solid var(--hj-green)', borderRadius: 999, padding: '1px 7px' }}><CheckCircle className="w-2.5 h-2.5" /> recorded</span>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                            <div className="flex items-center gap-2" style={{ padding: '10px 15px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
                              {isApplied ? (
                                <span className="inline-flex items-center gap-1.5 mono-label" style={{ color: 'var(--hj-green)' }}><CheckCircle className="w-3.5 h-3.5" /> Applied</span>
                              ) : superseded ? (
                                <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Superseded by a newer draft</span>
                              ) : (
                                <>
                                  <button type="button" onClick={() => applyCourseOutline(i, m.outline!)} style={{ ...primaryBtn, minHeight: 40, padding: '9px 18px', fontSize: 14 }}>Apply to curriculum</button>
                                  <button type="button" onClick={() => courseChatInputRef.current?.focus()} style={{ ...ghostBtn, minHeight: 40, padding: '9px 16px', fontSize: 14 }}>Keep chatting</button>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                  {courseChatBusy && <div className="inline-flex items-center gap-1.5" style={{ alignSelf: 'flex-start', fontSize: 12.5, color: 'var(--hj-ink-3)' }}><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</div>}
                </div>
                <div style={{ padding: 12, borderTop: '1px solid var(--hj-hairline-soft)' }}>
                  <div className="flex items-end gap-2">
                    <div data-paper="beige" style={{ flex: 1, borderRadius: 'var(--hj-r-sm)', overflow: 'hidden' }}>
                      <Textarea ref={courseChatInputRef} value={courseChatInput} onChange={e => setCourseChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendCourseChat(); } }} rows={1} placeholder="Tell me what to build or change…" style={{ resize: 'none', minHeight: 40 }} />
                    </div>
                    <button type="button" onClick={sendCourseChat} disabled={!courseChatInput.trim() || courseChatBusy} className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', cursor: 'pointer', opacity: (!courseChatInput.trim() || courseChatBusy) ? 0.5 : 1 }}><Send className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-center" style={{ marginTop: 20 }}>
                <button type="button" onClick={() => setPhase(modules.length ? 'outline' : 'idea')} style={ghostBtn}><ArrowLeft className="w-4 h-4" /> {modules.length ? 'Back to outline' : 'Back'}</button>
              </div>
            </div>
          )}

          {phase === 'lesson' && current && (
            <div>
              <div className="mono-label text-center" style={{ color: 'var(--hj-ink-3)' }}>{current.moduleTitle}</div>
              <h1 className="font-hj-display m-0 text-center" style={{ marginTop: 8, fontSize: 'clamp(21px, 2.8vw, 28px)', color: 'var(--hj-ink)', letterSpacing: '-0.025em', fontWeight: 800, lineHeight: 1.08 }}>{current.lesson.title}</h1>
              {current.lesson.description && <p className="m-0 text-center" style={{ marginTop: 14, fontSize: 16, color: 'var(--hj-ink-2)', lineHeight: 1.55, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto' }}>{current.lesson.description}</p>}

              {recorded && (
                <div className="flex items-center justify-center" style={{ marginTop: 16 }}>
                  <span className="inline-flex items-center gap-1.5 mono-label" style={{ color: 'var(--hj-green)' }}><CheckCircle className="w-3.5 h-3.5" /> Recorded</span>
                </div>
              )}

              {/* Coaching */}
              <div style={{ marginTop: 22 }}>
                {!chatOpen ? (
                  <div className="flex items-center justify-center">
                    <button type="button" onClick={openChat} style={ghostBtn}><MessageSquare className="w-4 h-4" /> Talk it through / get help</button>
                  </div>
                ) : (
                  <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', overflow: 'hidden' }}>
                    <div className="flex flex-col gap-2.5" style={{ padding: 16, maxHeight: 280, overflowY: 'auto' }}>
                      {chatMessages.map((m, i) => (
                        <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%', padding: '9px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.5, background: m.role === 'user' ? 'var(--hj-blue)' : 'var(--hj-surface-2)', color: m.role === 'user' ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink)' }}>{m.content}</div>
                      ))}
                      {chatBusy && <div className="inline-flex items-center gap-1.5" style={{ alignSelf: 'flex-start', fontSize: 12.5, color: 'var(--hj-ink-3)' }}><Loader2 className="w-3.5 h-3.5 animate-spin" /> thinking…</div>}
                    </div>
                    <div style={{ padding: 12, borderTop: '1px solid var(--hj-hairline-soft)' }}>
                      <div className="flex items-center gap-2" style={{ marginBottom: 10 }}>
                        <button type="button" onClick={refineLesson} disabled={!!busyAction} style={{ ...ghostBtn, minHeight: 34, padding: '6px 12px', fontSize: 12.5 }}>{busyAction === 'refine' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Refine</button>
                        <button type="button" onClick={generateScript} disabled={!!busyAction} style={{ ...ghostBtn, minHeight: 34, padding: '6px 12px', fontSize: 12.5 }}>{busyAction === 'script' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} {scripts[current.lesson.id] ? 'Redo script' : 'Generate script'}</button>
                      </div>
                      <div className="flex items-end gap-2">
                        <div data-paper="beige" style={{ flex: 1, borderRadius: 'var(--hj-r-sm)', overflow: 'hidden' }}>
                          <Textarea value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} rows={1} placeholder="Ask how to teach this…" style={{ resize: 'none', minHeight: 40 }} />
                        </div>
                        <button type="button" onClick={sendChat} disabled={!chatInput.trim() || chatBusy} className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 999, background: 'var(--hj-blue)', border: 0, color: 'oklch(0.18 0.005 70)', cursor: 'pointer', opacity: (!chatInput.trim() || chatBusy) ? 0.5 : 1 }}><Send className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Generated script — printed + editable; this is what feeds the teleprompter */}
              {scripts[current.lesson.id] !== undefined && (
                <div style={{ marginTop: 22 }}>
                  <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', marginBottom: 8 }}>
                    <FileText className="w-3.5 h-3.5" style={{ color: 'var(--hj-green)' }} /> Your script
                  </div>
                  <div data-paper="beige" style={{ borderRadius: 'var(--hj-r-md)', overflow: 'hidden', border: '1px solid var(--hj-beige-line)' }}>
                    <Textarea
                      value={scripts[current.lesson.id]}
                      onChange={e => setScripts(s => ({ ...s, [current.lesson.id]: e.target.value }))}
                      rows={8}
                      style={{ resize: 'vertical', fontSize: 15, lineHeight: 1.65 }}
                    />
                  </div>
                  <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-3)' }}>Read it, make it yours — this is exactly what scrolls on the teleprompter when you record.</p>
                </div>
              )}

              {/* Primary actions */}
              <div className="flex flex-col items-center gap-3" style={{ marginTop: 28 }}>
                <button type="button" onClick={() => setRecording(true)} disabled={uploadingLesson} style={{ ...primaryBtn, opacity: uploadingLesson ? 0.6 : 1 }}>
                  {uploadingLesson ? <><Loader2 className="w-4 h-4 animate-spin" /> Working…</> : <><VideoIcon className="w-4 h-4" /> {recorded ? 'Re-record this lesson' : 'Record this lesson'}</>}
                </button>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <label style={{ ...ghostBtn, cursor: 'pointer' }}><Upload className="w-4 h-4" /> Upload instead<input type="file" accept=".pdf,.png,.jpg,.jpeg,.mp4,.mov,.mp3,.wav" className="hidden" onChange={handleUpload} /></label>
                  <button type="button" onClick={bringOnGuest} disabled={startingInterview} style={ghostBtn}>{startingInterview ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Bring on a guest</button>
                  {recorded && current.lesson.fileType === 'video' && (
                    <button type="button" onClick={() => setEditTarget({ moduleId: current.moduleId, lessonId: current.lesson.id, s3Key: current.lesson.s3Key, title: current.lesson.title })} style={ghostBtn}><VideoIcon className="w-4 h-4" /> Edit video</button>
                  )}
                  <button type="button" onClick={advance} style={ghostBtn}>{recorded ? 'Next lesson' : 'Skip for now'} <ArrowRight className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          )}

          {phase === 'done' && (
            <div className="text-center">
              <CheckCircle className="mx-auto" style={{ width: 56, height: 56, color: 'var(--hj-green)' }} />
              <h1 className="font-hj-display m-0" style={{ marginTop: 18, fontSize: 'clamp(21px, 2.8vw, 28px)', color: 'var(--hj-ink)', letterSpacing: '-0.025em', fontWeight: 800 }}>Your course is built.</h1>
              <p className="m-0" style={{ marginTop: 12, fontSize: 16, color: 'var(--hj-ink-2)', lineHeight: 1.55 }}>{recordedCount} of {flat.length} lesson{flat.length !== 1 ? 's' : ''} recorded. Review everything and publish when you're ready.</p>
              <div className="flex items-center justify-center gap-3 flex-wrap" style={{ marginTop: 28 }}>
                <Link to="/my-store/studio" target="_blank" style={ghostBtn}><Sparkles className="w-4 h-4" /> Make a cover</Link>
                <button type="button" onClick={onClose} style={primaryBtn}>Review &amp; publish <ArrowRight className="w-4 h-4" /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer nav (lesson phase) */}
      {phase === 'lesson' && (
        <div className="flex items-center justify-between flex-shrink-0" style={{ padding: '14px 20px', borderTop: '1px solid var(--hj-hairline-soft)' }}>
          <button type="button" onClick={back} style={ghostBtn}><ArrowLeft className="w-4 h-4" /> Back</button>
          <button type="button" onClick={() => setPhase('done')} style={{ ...ghostBtn, border: 0 }}>Finish up</button>
        </div>
      )}

      <RecordingModal open={recording} onClose={() => setRecording(false)} onSave={handleRecorded} initialScript={current ? scripts[current.lesson.id] : undefined} />

      {/* Inline video edit drawer (single panel-level instance) */}
      {editTarget && (
        <VideoEditDrawer
          open
          video={{ s3Key: editTarget.s3Key, title: editTarget.title }}
          onApplied={(patch) => updateLesson(editTarget.moduleId, editTarget.lessonId, patch)}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
